import mitt from "@webcore/core/events/mitt";
import type { TransformNode } from "babylonjs";
import { AppMode, AppState } from "@core/context/types";
import type { TrackingStatus } from "@core/context/types";
import { SubtitleCue } from "@core/utils/subtitles/types";

// Re-export types for convenience
export { AppMode, AppState };
export type { TrackingStatus };

// ============================================================================
// TYPES
// ============================================================================

// XR Session state enum
export enum XRSessionState {
  IDLE = 'idle',
  READY = 'ready',
  STARTING = 'starting',
  RUNNING = 'running',
  STOPPING = 'stopping',
  ERROR = 'error',
}

export type XRErrorType = 'undefined' | 'not-supported' | 'permission-denied';
export interface XRSessionErrorInfo {
  type: XRErrorType;
  permissionType?: string;
  message?: string;
}

export type AppEvents = {
  'preview-close': void;
  'preview-share': void;
  'switch-modes': "twin" | "ground" | "image" | "vps";
  'content-positioning': void;
  'content-positioned': void;
  'xr-ready': void;
  'tracking-status-changed': TrackingStatus;
  'ground-tracking-status-changed': TrackingStatus;
  'app-mode-changed': AppMode;
  'app-state-changed': AppState;
  'view-3d': boolean;
  'prepare-runtime': void;

  'anchor-created': TransformNode;
  'anchor-removed': TransformNode;

  'content-found': { targetName: string };
  'content-hidden': { targetName: string };
  'content-lost': { targetName: string };
  'content-update': { targetName: string };
  'content-visible': { targetName: string };

  '8w-killed': void;
  '8w-initialized': void;
  'camera-ready': void;
  'permissions-granted': void;
  'geo-passed': void;
  'content-id-changing': string; // Emitted when content ID is about to change
  'content-switch-complete': AppState; // Emitted when content switch is done, carries target state for UI
  'animation-loop-complete': void; // Emitted when one loop of animation completes (does NOT auto-restart)
  'content-loop': void;    // Request to restart content from beginning (manual loop trigger)
  'content-stop': void;    // Request to stop, reset, and hide current content (used by overlays)
  'content-pause': void;   // Request to pause current content playback
  'content-resume': void;  // Request to resume current content playback
  'content-hide': void;    // Request to hide current content
  'content-show': void;    // Request to show and reposition current content
  'subtitle-cue': SubtitleCue | null; // Emitted when subtitle cue changes during playback
  
  // XR Session state (from XRSessionController)
  'xr-session-state-changed': { 
    prev: XRSessionState; 
    current: XRSessionState; 
    error: XRSessionErrorInfo | null;
  };
  'content-rotation-changed': number; // Emitted when content rotation changes significantly
  
  // Video mute control (VideoAR integration)
  'video-mute-toggle': boolean;   // Request to set video muted state
  'video-muted-change': boolean;  // Emitted when video muted state changes
  
  // Video loop detection
  'video-loop': void;  // Emitted when video completes a loop
};

const appEvents = mitt<AppEvents>();

export default appEvents;
